import { useState } from 'react';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Button } from '../../../components/ui/button';
import { Badge } from '../../../components/ui/badge';
import { BadgeCheck, Clock, Loader2, ShieldX } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../../lib/api';
import { AuthImage } from './AuthImage';
import { errText } from './errText';

const STATUS = {
  verified: { label: 'Ghana Card verified', cls: 'bg-emerald-100 text-emerald-800', Icon: BadgeCheck },
  pending_review: { label: 'Ghana Card photo waiting for pharmacist', cls: 'bg-amber-100 text-amber-800', Icon: Clock },
};

const CaptureForm = ({ patientId, form, onDone }) => {
  const [expiry, setExpiry] = useState('');
  const [card, setCard] = useState(null);
  const [selfie, setSelfie] = useState(null);
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (!card || !selfie || !expiry) { toast.error('Add the Ghana Card photo, a photo of the customer and the card expiry date'); return; }
    const body = new FormData();
    Object.entries({ patient_id: patientId, id_type: 'ghana_card', full_name: form.full_name, id_number: form.ghana_card_number,
      id_expiry: expiry, residential_address: form.address, phone: form.phone }).forEach(([k, v]) => body.append(k, v));
    body.append('id_image', card);
    body.append('selfie', selfie);
    setBusy(true);
    try { await api.post('/staff/identity/counter', body, { headers: { 'Content-Type': 'multipart/form-data' } }); toast.success('Ghana Card photo sent for pharmacist review'); onDone(); }
    catch (e) { toast.error(errText(e, 'Could not save the Ghana Card photo')); }
    finally { setBusy(false); }
  };
  return (
    <div className="space-y-2 rounded-md border border-dashed p-3" data-testid="ctrl-ghana-capture">
      <p className="text-xs font-medium">Capture the Ghana Card (uses the number typed above)</p>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
        <div><Label className="text-[11px]">Card expiry *</Label><Input type="date" value={expiry} onChange={(e) => setExpiry(e.target.value)} data-testid="ctrl-ghana-expiry" /></div>
        <div><Label className="text-[11px]">Card photo *</Label><Input type="file" accept="image/*" capture="environment" onChange={(e) => setCard(e.target.files[0])} data-testid="ctrl-ghana-photo" /></div>
        <div><Label className="text-[11px]">Customer photo *</Label><Input type="file" accept="image/*" capture="user" onChange={(e) => setSelfie(e.target.files[0])} data-testid="ctrl-ghana-selfie" /></div>
      </div>
      <Button size="sm" variant="outline" onClick={submit} disabled={busy} data-testid="ctrl-ghana-submit">
        {busy && <Loader2 className="h-3 w-3 animate-spin mr-1" />} Save Ghana Card photo
      </Button>
    </div>
  );
};

export const GhanaCardCheck = ({ patientId, identity, form, canApprove, onChanged }) => {
  const [busy, setBusy] = useState(false);
  const meta = STATUS[identity?.status] || { label: 'Ghana Card not verified', cls: 'bg-red-100 text-red-800', Icon: ShieldX };
  const verify = async () => {
    setBusy(true);
    try { await api.post(`/staff/identity/${identity.verification_id}/decision`, { decision: 'verified' }); toast.success('Ghana Card verified'); onChanged(); }
    catch (e) { toast.error(errText(e, 'Could not verify')); }
    finally { setBusy(false); }
  };
  const isGhanaCard = identity?.id_type === 'ghana_card' || !identity?.id_type;
  return (
    <div className="space-y-2" data-testid="ctrl-ghana-check">
      <Badge className={`${meta.cls} gap-1`} data-testid="ctrl-ghana-status"><meta.Icon className="h-3 w-3" />{meta.label}</Badge>
      {identity?.status === 'pending_review' && canApprove && (
        <div className="flex items-center gap-3">
          <AuthImage src={`/staff/identity/${identity.verification_id}/image/id`} alt="Ghana Card" className="h-20 rounded border" />
          <Button size="sm" onClick={verify} disabled={busy} data-testid="ctrl-ghana-verify">Card matches — verify</Button>
        </div>
      )}
      {(!identity || !['verified', 'pending_review'].includes(identity.status) || !isGhanaCard) && (
        <CaptureForm patientId={patientId} form={form} onDone={onChanged} />
      )}
    </div>
  );
};
